import { useState } from 'react';

import Icon from '../../../components/AppIcon';

const ChangePasswordModal = ({ onConfirm }) => {
  const isOpen = true;
  const [formValues, setFormValues] = useState({
    oldPassword: '',
    newPassword: '',
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [strength, setStrength] = useState('');

  const checkStrength = (pwd) => {
    if (!pwd) return '';
    if (pwd.length < 6) return 'Weak';
    if (/^(?=.*[A-Z])(?=.*\d)(?=.*[a-z]).{8,}$/.test(pwd)) return 'Strong';
    return 'Medium';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'newPassword' && value.length > 12) return;

    setFormValues((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({ ...prev, [name]: '' }));

    if (name === 'newPassword') setStrength(checkStrength(value));
  };

  const validatePasswords = () => {
    const { oldPassword, newPassword } = formValues;
    const validationErrors = {};

    // Old password validation
    if (!oldPassword.trim()) validationErrors.oldPassword = 'Old password is required.';

    // New password validation
    if (!newPassword.trim()) validationErrors.newPassword = 'New password is required.';
    else {
      if (newPassword.length < 8)
        validationErrors.newPassword = 'Password must be at least 8 characters long.';
      else if (newPassword.length > 12)
        validationErrors.newPassword = 'Password must not exceed 12 characters.';
      else if (!/[A-Z]/.test(newPassword))
        validationErrors.newPassword = 'Password must contain at least one uppercase letter.';
      else if (!/[a-z]/.test(newPassword))
        validationErrors.newPassword = 'Password must contain at least one lowercase letter.';
      else if (!/\d/.test(newPassword))
        validationErrors.newPassword = 'Password must include at least one number.';
      else if (!/[@$!%*?&#]/.test(newPassword))
        validationErrors.newPassword = 'Password must include at least one special character.';
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleConfirm = () => {
    if (!validatePasswords()) return;
    onConfirm(formValues);
  };

  if (!isOpen) return null;

  const renderPasswordField = (
    label,
    name,
    value,
    showPassword,
    setShowPassword,
    isNewPassword = false,
  ) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-text-primary mb-2">{label}</label>
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={handleChange}
          maxLength={12} // Limit the input length to 12 characters
          placeholder={`Enter your ${label.toLowerCase()}`}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 pr-10 ${
            errors[name] ? 'border-red-500' : 'border-border'
          }`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-3 flex items-center text-text-secondary hover:text-text-primary focus:outline-none"
          tabIndex={-1}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
        </button>
      </div>

      {/* Field-specific error */}
      {errors[name] && (
        <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
          <Icon name="AlertCircle" size={14} />
          <span>{errors[name]}</span>
        </p>
      )}

      {/* Password strength */}
      {isNewPassword && strength && !errors.newPassword && (
        <div className="mt-3">
          <div className="flex justify-between text-xs font-medium text-text-secondary mb-1">
            <span>Password Strength:</span>
            <span
              className={
                strength === 'Weak'
                  ? 'text-red-500'
                  : strength === 'Medium'
                    ? 'text-yellow-500'
                    : 'text-green-500'
              }
            >
              {strength}
            </span>
          </div>
          <div className="w-full h-2 bg-secondary-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                strength === 'Weak'
                  ? 'bg-red-500 w-1/3'
                  : strength === 'Medium'
                    ? 'bg-yellow-500 w-2/3'
                    : 'bg-green-500 w-full'
              }`}
            ></div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle bg-surface shadow-xl rounded-2xl border border-border transform transition-all z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Set Your Password</h3>
            </div>
          </div>

          {renderPasswordField(
            'Old Password',
            'oldPassword',
            formValues.oldPassword,
            showOldPassword,
            setShowOldPassword,
          )}
          {renderPasswordField(
            'New Password',
            'newPassword',
            formValues.newPassword,
            showNewPassword,
            setShowNewPassword,
            true,
          )}

          <div className="flex justify-end mt-4">
            <button
              onClick={handleConfirm}
              className="flex items-center justify-center px-4 py-2 text-white bg-primary hover:bg-primary-600 rounded-lg transition-colors duration-200 space-x-2"
            >
              <Icon name="Lock" size={16} />
              <span>Save Password</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
